// by freakms - ich schwöre feierlich ich bin ein tunichtgut
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Tag,
  Plus,
  Trash2,
  Edit,
  Search,
  Calendar,
  Store,
  Package,
  Percent,
  Link as LinkIcon,
  Settings,
  ToggleLeft,
  ToggleRight,
  Euro,
  AlertTriangle,
  ChevronRight,
  ShoppingBag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const DISCOUNTERS = ["Aldi", "Lidl", "Penny", "Kaufland", "Rewe", "Edeka", "Netto"];

// ============================================================
// TAB 1: Automat-Rabattfächer
// ============================================================

const DiscountSlotCard = ({ compartment, product, batch, onToggleDiscount, onSetPrice, onClearPrice }) => {
  const [editingPrice, setEditingPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(compartment.price_override?.toString() || "");
  const [saving, setSaving] = useState(false);

  const mhd = batch?.expiry_date && batch.expiry_date !== "1970-01-01"
    ? (() => {
        const days = Math.ceil((new Date(batch.expiry_date) - new Date()) / 86400000);
        return { days, label: days < 0 ? "abgelaufen" : `${days}T`, urgent: days <= 14 };
      })()
    : null;

  const discountPercent = product && compartment.price_override
    ? Math.round((1 - compartment.price_override / product.price) * 100)
    : null;

  const handleSavePrice = async () => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) { toast.error("Ungültiger Preis"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/compartments/${compartment.id}`, { price_override: price || null });
      toast.success(`Preis für Fach ${compartment.slot_id} gesetzt`);
      onSetPrice();
      setEditingPrice(false);
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const handleClearPrice = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/compartments/${compartment.id}`, { price_override: null });
      toast.success("Rabattpreis entfernt");
      onClearPrice();
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <Card className={`border ${compartment.is_discount_slot ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Links: Fach-Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold text-lg">{compartment.slot_id}</span>
              {compartment.is_discount_slot && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300">Rabattfach</Badge>
              )}
              {compartment.current_stock === 0 && (
                <Badge variant="outline" className="text-slate-400">Leer</Badge>
              )}
            </div>

            <div className="mt-1 font-medium text-slate-800 truncate">
              {product?.name || <span className="text-slate-400 italic">Kein Produkt</span>}
            </div>

            <div className="flex items-center gap-3 mt-1 flex-wrap text-sm">
              <span className="text-slate-500">{compartment.current_stock}/{compartment.max_capacity} Stück</span>
              {mhd && (
                <span className={`px-2 py-0.5 rounded text-xs border ${mhd.urgent ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                  MHD: {new Date(batch.expiry_date).toLocaleDateString("de-DE")} ({mhd.label})
                </span>
              )}
            </div>
          </div>

          {/* Rechts: Preis */}
          <div className="text-right shrink-0">
            {product && (
              <div className="text-xs text-slate-400 line-through">{product.price.toFixed(2)}€</div>
            )}
            {compartment.price_override ? (
              <div>
                <div className="text-xl font-heading font-bold text-amber-600">
                  {compartment.price_override.toFixed(2)}€
                </div>
                {discountPercent > 0 && (
                  <div className="text-xs text-amber-600 flex items-center justify-end gap-1">
                    <Percent className="w-3 h-3" />-{discountPercent}%
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xl font-heading font-bold text-slate-600">
                {product?.price.toFixed(2) || "–"}€
              </div>
            )}
          </div>
        </div>

        {/* Aktionen */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200 flex-wrap">
          {/* Rabattfach Toggle */}
          <Button
            size="sm"
            variant="outline"
            className={compartment.is_discount_slot
              ? "border-amber-300 text-amber-700 hover:bg-amber-100"
              : "text-slate-600"}
            onClick={() => onToggleDiscount(compartment)}
          >
            {compartment.is_discount_slot
              ? <><ToggleRight className="w-4 h-4 mr-1" /> Rabattfach aktiv</>
              : <><ToggleLeft className="w-4 h-4 mr-1" /> Als Rabattfach</>
            }
          </Button>

          {/* Preis setzen */}
          {editingPrice ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="h-8 w-24 text-sm"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") handleSavePrice(); if (e.key === "Escape") setEditingPrice(false); }}
              />
              <Button size="sm" onClick={handleSavePrice} disabled={saving} className="h-8 bg-amber-500 hover:bg-amber-600">
                {saving ? "..." : "OK"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingPrice(false)} className="h-8">✕</Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="text-slate-600"
              onClick={() => { setNewPrice(compartment.price_override?.toString() || (product ? (product.price * 0.8).toFixed(2) : "")); setEditingPrice(true); }}
            >
              <Euro className="w-4 h-4 mr-1" />
              {compartment.price_override ? "Preis ändern" : "Rabattpreis setzen"}
            </Button>
          )}

          {/* Preis löschen */}
          {compartment.price_override && !editingPrice && (
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-rose-600"
              onClick={handleClearPrice}
              disabled={saving}
            >
              Rabatt entfernen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const DiscountSlotsTab = () => {
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | discount | price

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [compRes, prodRes, batchRes, settingsRes] = await Promise.all([
        axios.get(`${API}/compartments`),
        axios.get(`${API}/products`),
        axios.get(`${API}/inventory/batches`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} }))
      ]);
      setCompartments(compRes.data);
      setProducts(prodRes.data);
      setBatches(batchRes.data);
      setSettings(settingsRes.data);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const handleToggleDiscount = async (compartment) => {
    try {
      await axios.put(`${API}/compartments/${compartment.id}`, {
        is_discount_slot: !compartment.is_discount_slot
      });
      toast.success(compartment.is_discount_slot ? "Rabattfach deaktiviert" : "Als Rabattfach markiert");
      fetchData();
    } catch { toast.error("Fehler"); }
  };

  const productMap = Object.fromEntries(products.map(p => [p.id, p]));
  const batchMap = Object.fromEntries(batches.map(b => [b.id, b]));

  const filledCompartments = compartments
    .filter(c => c.product_id)
    .sort((a, b) => a.slot_id.localeCompare(b.slot_id));

  const filtered = filledCompartments.filter(c => {
    if (filter === "discount") return c.is_discount_slot;
    if (filter === "price") return c.price_override != null;
    return true;
  });

  const discountSlotCount = filledCompartments.filter(c => c.is_discount_slot).length;
  const priceOverrideCount = filledCompartments.filter(c => c.price_override != null).length;

  // Konfigurierte Rabattfächer aus Settings
  const configuredSlots = settings?.discount_slot_ids
    ? settings.discount_slot_ids.split(",").map(s => s.trim()).filter(Boolean)
    : ["A1", "A2", "A3"];

  if (loading) return <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Info-Banner: konfigurierte Rabattfächer */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-amber-800">Konfigurierte Rabattfächer</div>
              <div className="text-sm text-amber-700 mt-1">
                Fächer <strong>{configuredSlots.join(", ")}</strong> werden beim Wareneingang als Rabattfach vorgeschlagen wenn ein MHD-Konflikt erkannt wird.
              </div>
              <div className="text-xs text-amber-600 mt-1">
                Änderbar in den Einstellungen unter <code className="bg-amber-100 px-1 rounded">discount_slot_ids</code>.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter + Stats */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>
          Alle ({filledCompartments.length})
        </Button>
        <Button size="sm" variant={filter === "discount" ? "default" : "outline"} className={filter === "discount" ? "bg-amber-500 hover:bg-amber-600" : ""} onClick={() => setFilter("discount")}>
          <Tag className="w-3 h-3 mr-1" /> Rabattfächer ({discountSlotCount})
        </Button>
        <Button size="sm" variant={filter === "price" ? "default" : "outline"} className={filter === "price" ? "bg-amber-500 hover:bg-amber-600" : ""} onClick={() => setFilter("price")}>
          <Euro className="w-3 h-3 mr-1" /> Mit Rabattpreis ({priceOverrideCount})
        </Button>
      </div>

      {/* Fächer-Grid */}
      {filtered.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Keine Fächer gefunden</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(comp => (
            <DiscountSlotCard
              key={comp.id}
              compartment={comp}
              product={productMap[comp.product_id]}
              batch={batchMap[comp.active_batch_id]}
              onToggleDiscount={handleToggleDiscount}
              onSetPrice={fetchData}
              onClearPrice={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// TAB 2: Discounter-Angebote (bisherige Seite)
// ============================================================

const DiscounterOffersTab = ({ products }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDiscounter, setFilterDiscounter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [offerToMatch, setOfferToMatch] = useState(null);

  const [formData, setFormData] = useState({
    product_name: "", ean: "", discounter: "",
    regular_price: "", offer_price: "",
    valid_from: new Date().toISOString().split("T")[0],
    valid_until: "", matched_product_id: "", notes: ""
  });

  useEffect(() => { fetchOffers(); }, []);

  const fetchOffers = async () => {
    try {
      const res = await axios.get(`${API}/discount-offers?active_only=false`);
      setOffers(res.data.offers || []);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        regular_price: parseFloat(formData.regular_price) || 0,
        offer_price: parseFloat(formData.offer_price) || 0,
        matched_product_id: formData.matched_product_id || null
      };
      if (editingOffer) {
        await axios.put(`${API}/discount-offers/${editingOffer.id}`, data);
        toast.success("Angebot aktualisiert");
      } else {
        await axios.post(`${API}/discount-offers`, data);
        toast.success("Angebot erstellt");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchOffers();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Angebot wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/discount-offers/${id}`);
      toast.success("Angebot gelöscht");
      fetchOffers();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      product_name: offer.product_name, ean: offer.ean || "",
      discounter: offer.discounter,
      regular_price: offer.regular_price?.toString() || "",
      offer_price: offer.offer_price?.toString() || "",
      valid_from: offer.valid_from, valid_until: offer.valid_until,
      matched_product_id: offer.matched_product_id || "", notes: offer.notes || ""
    });
    setIsDialogOpen(true);
  };

  const handleMatch = async (productId) => {
    if (!offerToMatch) return;
    try {
      await axios.post(`${API}/discount-offers/match/${offerToMatch.id}/${productId}`);
      toast.success("Produkt verknüpft");
      setMatchDialogOpen(false);
      setOfferToMatch(null);
      fetchOffers();
    } catch { toast.error("Fehler beim Verknüpfen"); }
  };

  const resetForm = () => {
    setEditingOffer(null);
    setFormData({
      product_name: "", ean: "", discounter: "",
      regular_price: "", offer_price: "",
      valid_from: new Date().toISOString().split("T")[0],
      valid_until: "", matched_product_id: "", notes: ""
    });
  };

  const filteredOffers = offers.filter(offer => {
    const matchesDiscounter = filterDiscounter === "all" || offer.discounter === filterDiscounter;
    const matchesSearch = !searchTerm ||
      offer.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.matched_product_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDiscounter && matchesSearch;
  });

  const isExpired = (dateStr) => new Date(dateStr) < new Date();
  const isActive = (offer) => !isExpired(offer.valid_until) && offer.is_active;

  if (loading) return <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Filter + Neues Angebot */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Produkt suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={filterDiscounter} onValueChange={setFilterDiscounter}>
          <SelectTrigger className="w-full sm:w-44">
            <Store className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Discounter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Discounter</SelectItem>
            {DISCOUNTERS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" /> Neues Angebot
        </Button>
      </div>

      {/* Angebote */}
      <div className="grid gap-3">
        {filteredOffers.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-10 text-center">
              <ShoppingBag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Keine Angebote gefunden</p>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Erstes Angebot anlegen
              </Button>
            </CardContent>
          </Card>
        ) : filteredOffers.map((offer, index) => (
          <motion.div key={offer.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
            <Card className={`border-slate-200 ${isExpired(offer.valid_until) ? "opacity-60" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{offer.product_name}</h3>
                      <Badge variant={isActive(offer) ? "default" : "secondary"} className={isActive(offer) ? "bg-emerald-100 text-emerald-700" : ""}>
                        {isActive(offer) ? "Aktiv" : "Abgelaufen"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-sm">
                      <Badge variant="outline" className="gap-1"><Store className="w-3 h-3" />{offer.discounter}</Badge>
                      <span className="text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(offer.valid_from).toLocaleDateString("de-DE")} – {new Date(offer.valid_until).toLocaleDateString("de-DE")}
                      </span>
                    </div>
                    {offer.matched_product_name ? (
                      <div className="mt-1 flex items-center gap-2 text-sm">
                        <LinkIcon className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-700 font-medium">{offer.matched_product_name}</span>
                      </div>
                    ) : (
                      <button className="mt-1 text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                        onClick={() => { setOfferToMatch(offer); setMatchDialogOpen(true); }}>
                        <LinkIcon className="w-3 h-3" /> Verknüpfen
                      </button>
                    )}
                  </div>
                  <div className="text-right flex flex-col items-end gap-2 shrink-0">
                    {offer.regular_price > 0 && <p className="text-sm text-slate-400 line-through">{offer.regular_price.toFixed(2)}€</p>}
                    <p className="text-2xl font-bold text-emerald-600">{offer.offer_price.toFixed(2)}€</p>
                    {offer.regular_price > 0 && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1">
                        <Percent className="w-3 h-3" />{Math.round((1 - offer.offer_price / offer.regular_price) * 100)}%
                      </p>
                    )}
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => handleEdit(offer)} className="h-8 w-8"><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(offer.id)} className="h-8 w-8 text-rose-600"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
                {offer.notes && <p className="text-sm text-slate-500 mt-2 pt-2 border-t">{offer.notes}</p>}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Angebot anlegen/bearbeiten */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOffer ? "Angebot bearbeiten" : "Neues Angebot"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Produktname beim Discounter *</Label>
                <Input value={formData.product_name} onChange={(e) => setFormData({...formData, product_name: e.target.value})} placeholder="z.B. Coca-Cola 1,5L" required />
              </div>
              <div>
                <Label>Discounter *</Label>
                <Select value={formData.discounter} onValueChange={(v) => setFormData({...formData, discounter: v})} required>
                  <SelectTrigger><SelectValue placeholder="Wählen..." /></SelectTrigger>
                  <SelectContent>{DISCOUNTERS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>EAN (optional)</Label>
                <Input value={formData.ean} onChange={(e) => setFormData({...formData, ean: e.target.value})} placeholder="4001234567890" />
              </div>
              <div>
                <Label>Normalpreis (€)</Label>
                <Input type="number" step="0.01" value={formData.regular_price} onChange={(e) => setFormData({...formData, regular_price: e.target.value})} placeholder="0.00" />
              </div>
              <div>
                <Label>Angebotspreis (€) *</Label>
                <Input type="number" step="0.01" value={formData.offer_price} onChange={(e) => setFormData({...formData, offer_price: e.target.value})} placeholder="0.00" required />
              </div>
              <div>
                <Label>Gültig ab *</Label>
                <Input type="date" value={formData.valid_from} onChange={(e) => setFormData({...formData, valid_from: e.target.value})} required />
              </div>
              <div>
                <Label>Gültig bis *</Label>
                <Input type="date" value={formData.valid_until} onChange={(e) => setFormData({...formData, valid_until: e.target.value})} required />
              </div>
              <div className="col-span-2">
                <Label>Mit eigenem Produkt verknüpfen</Label>
                <Select value={formData.matched_product_id || "none"} onValueChange={(v) => setFormData({...formData, matched_product_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Produkt wählen (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Verknüpfung</SelectItem>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notizen</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">{editingOffer ? "Speichern" : "Anlegen"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Produkt verknüpfen */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader><DialogTitle>Produkt verknüpfen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-3">„{offerToMatch?.product_name}" mit eigenem Produkt verknüpfen:</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {products.map(product => (
              <button key={product.id} onClick={() => handleMatch(product.id)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left">
                <div>
                  <p className="font-medium">{product.name}</p>
                  {product.ean && <p className="text-xs text-slate-400">EAN: {product.ean}</p>}
                </div>
                <p className="text-sm text-slate-500">{product.price?.toFixed(2)}€</p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialogOpen(false)}>Abbrechen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// HAUPT-SEITE
// ============================================================

const DiscountOffersPage = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="discount-offers-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Rabatte & Angebote</h1>
        <p className="text-slate-500 mt-1">Rabattfächer im Automaten und Discounter-Angebote</p>
      </div>

      <Tabs defaultValue="slots">
        <TabsList className="mb-4">
          <TabsTrigger value="slots" className="flex items-center gap-2">
            <Tag className="w-4 h-4" /> Rabattfächer
          </TabsTrigger>
          <TabsTrigger value="offers" className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" /> Discounter-Angebote
          </TabsTrigger>
        </TabsList>

        <TabsContent value="slots">
          <DiscountSlotsTab />
        </TabsContent>

        <TabsContent value="offers">
          <DiscounterOffersTab products={products} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DiscountOffersPage;
